/**
 * Generate weekday night slots for the first two weeks of December
 * Weekday nights are Monday through Friday evenings
 */

export interface DateSlot {
  id: string;
  date: string; // ISO date string (YYYY-MM-DD)
  dayName: string; // e.g., "Monday", "Tuesday"
  displayDate: string; // e.g., "Dec 2"
}

/**
 * Get the first Monday of December
 */
function getFirstMondayOfDecember(year: number): Date {
  const dec1 = new Date(year, 11, 1); // Month is 0-indexed, so 11 = December
  const dayOfWeek = dec1.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

  // Calculate days to add to get to the first Monday
  // If Dec 1 is Monday (1), add 0 days
  // If Dec 1 is Tuesday (2), add 6 days (go to next Monday)
  // If Dec 1 is Wednesday (3), add 5 days
  // If Dec 1 is Thursday (4), add 4 days
  // If Dec 1 is Friday (5), add 3 days
  // If Dec 1 is Saturday (6), add 2 days
  // If Dec 1 is Sunday (0), add 1 day
  const daysToAdd = dayOfWeek === 0 ? 1 : dayOfWeek === 1 ? 0 : 8 - dayOfWeek;

  const firstMonday = new Date(dec1);
  firstMonday.setDate(dec1.getDate() + daysToAdd);
  return firstMonday;
}

/**
 * Get all weekday night slots for the first two weeks of December
 * This function calculates the dates dynamically based on the current year
 */
export function getWeekdayNightSlots(): DateSlot[] {
  const currentYear = new Date().getFullYear();
  const slots: DateSlot[] = [];

  // Get the first Monday of December
  const mondayOfWeek1 = getFirstMondayOfDecember(currentYear);
  // Get the second Monday (first Monday + 7 days)
  const mondayOfWeek2 = new Date(mondayOfWeek1);
  mondayOfWeek2.setDate(mondayOfWeek1.getDate() + 7);

  // Generate slots for both weeks (Monday through Friday, 5 days each)
  const mondays = [mondayOfWeek1, mondayOfWeek2];

  for (const monday of mondays) {
    for (let i = 0; i < 5; i++) { // Monday (0) through Friday (4)
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);

      const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

      // Monday = 1, Tuesday = 2, Wednesday = 3, Thursday = 4, Friday = 5
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

        const isoDate = date.toISOString().split("T")[0]!; // YYYY-MM-DD format
        const dayName = dayNames[dayOfWeek]!;
        const monthName = monthNames[date.getMonth()]!;

        slots.push({
          id: isoDate,
          date: isoDate,
          dayName,
          displayDate: `${monthName} ${date.getDate()}`,
        });
      }
    }
  }

  return slots;
}

/**
 * Get all available date slot IDs
 */
export function getAllDateSlotIds(): string[] {
  return getWeekdayNightSlots().map((slot) => slot.id);
}

